import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { tokens } from '../../../theme/tokens';
import { v2Mono } from './v2Ui';

interface Concept {
  concept: string;
  motzu: string;
  shopify: string;
  meaning: string;
  example: string;
  /** Rows about the Store Taxonomy are hidden when the store cannot use one (flag `merchant_taxonomy` off). */
  taxonomy?: boolean;
  /** Shown only with flag `name_rule_shopify_category` on (true) or off (false); absent shows in both. */
  ruleCategoryOnly?: boolean;
}

/**
 * The relationship table: each idea, what Motzu calls it, what Shopify's admin calls it, and one example
 * from the FX3 walk-through below. "Category" means three different things here, so this comes first.
 */
const CONCEPTS: Concept[] = [
  {
    concept: 'Store Taxonomy',
    motzu: 'Store Taxonomy → Subcategory',
    shopify: 'Not shown',
    meaning: 'Your own merchandising classification.',
    example: 'Cinema Cameras',
    taxonomy: true,
  },
  {
    concept: 'Resolver key',
    motzu: 'Code / SKU prefix',
    shopify: 'SKU',
    meaning: 'Connects the product to your Store Taxonomy.',
    example: 'CAM-121',
    taxonomy: true,
  },
  {
    concept: 'Shopify Product Type',
    motzu: 'Shopify Product Type',
    shopify: 'Type',
    meaning: 'Your own product grouping in Shopify.',
    example: 'Cinema Cameras',
  },
  {
    concept: 'Shopify Product Category',
    motzu: 'Shopify Product Category',
    shopify: 'Category',
    meaning: 'Shopify’s standard product taxonomy.',
    example: 'Cinema Video Cameras',
  },
  {
    concept: 'Motzu Classification',
    motzu: 'Motzu Classification',
    shopify: 'Not shown',
    meaning: 'What Motzu detects about the product from its own catalog data.',
    example: 'cinema-camera',
  },
  {
    concept: 'Name Rules (optional)',
    motzu: 'Name Rules',
    shopify: 'Not shown',
    meaning: 'A keyword in the product name that decides the type, and optionally the category.',
    example: 'fx3 → Cinema Cameras',
    ruleCategoryOnly: true,
  },
  {
    concept: 'Name Rules (optional)',
    motzu: 'Name Rules',
    shopify: 'Not shown',
    meaning: 'A keyword in the product name that decides the type when the overrides give none.',
    example: 'fx3 → Cinema Cameras',
    ruleCategoryOnly: false,
  },
  {
    concept: 'Classification overrides',
    motzu: 'Motzu Classification Overrides',
    shopify: 'Not shown',
    meaning: 'Maps a Motzu Classification to Shopify outputs when the Store Taxonomy does not decide it.',
    example: 'cinema-camera → Cinema Video Cameras',
  },
  {
    concept: 'Unmatched fallback',
    motzu: 'Unmatched Product Rules',
    shopify: 'Type / Category after sync',
    meaning: 'Last-resort behaviour when nothing earlier matched.',
    example: '[UNMAPPED]',
  },
];

/** The FX3 walk-through: one real product resolved by the Store Taxonomy, so no later step is used. */
const FX3_EXAMPLE: Array<[string, string]> = [
  ['Store SKU', 'CAM-121-00043'],
  ['Matched Store Taxonomy entry', 'CAM-121'],
  ['Store Taxonomy subcategory', 'Cinema Cameras'],
  ['Shopify Product Type', 'Cinema Cameras'],
  ['Shopify Product Category', 'Cinema Video Cameras'],
  ['Motzu Classification Overrides used?', 'No'],
  ['Unmatched Product Rules used?', 'No'],
];

const headSx = { fontWeight: 700, whiteSpace: 'nowrap' } as const;

interface MappingExplainerProps {
  /** Flag `merchant_taxonomy`: the Store Taxonomy step, its table rows and the FX3 example. */
  taxonomyOn: boolean;
  /**
   * Flag `name_rule_shopify_category`. On, Name Rules run before the overrides and may set the category; off, the
   * server (and `resolveTry`) runs the overrides first and a rule only sets the type of a product they left untyped.
   */
  ruleCategoryOn: boolean;
}

/** Body of "How mapping decides a product's category": relationship table, decision order, example, then each step. */
const MappingExplainer: React.FC<MappingExplainerProps> = ({ taxonomyOn, ruleCategoryOn }) => {
  const concepts = CONCEPTS.filter(
    (row) => (taxonomyOn || !row.taxonomy) && (row.ruleCategoryOnly === undefined || row.ruleCategoryOnly === ruleCategoryOn),
  );
  const middle = ruleCategoryOn
    ? ['Name Rules', 'Motzu Classification Overrides']
    : ['Motzu Classification Overrides', 'Name Rules'];
  const order = [
    ...(taxonomyOn ? ['Store SKU', 'Store Taxonomy'] : []),
    ...middle,
    'Unmatched Product Rules',
    'Shopify Product Type + Shopify Product Category',
  ];
  const nameRulesStep = (
    <li key="rules">
      {ruleCategoryOn ? (
        <>
          <strong>Name Rules</strong> (optional) pick a Shopify Product Type, and optionally a Shopify Product Category, from a keyword in the product name, before the overrides below.
        </>
      ) : (
        <>
          <strong>Name Rules</strong> (optional) pick a Shopify Product Type from a keyword in the product name. They run after the overrides above, so a rule only decides a product whose Motzu Classification has no Shopify Product Type there.
        </>
      )}{' '}
      Use them when your Store Taxonomy does not classify a product, or when you don&apos;t use a Store Taxonomy and need specific product names to override Motzu&apos;s classification. If your Store Taxonomy already classifies your products correctly, leave them empty.
    </li>
  );
  const overridesStep = (
    <li key="overrides">
      <strong>Motzu Classification Overrides</strong> have one row per Motzu Classification deciding its Shopify Product Type and Shopify Product Category. Use them to rename or regroup Motzu&apos;s classifications into your own words; the Type from column shows which step decided each row.
    </li>
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
        Motzu writes two fields to Shopify: the <strong>Shopify Product Type</strong> (Shopify calls it Type) and the{' '}
        <strong>Shopify Product Category</strong> (Shopify calls it Category). &ldquo;Category&rdquo; means different things in
        different places, so this table shows how the pieces relate before you configure anything.
      </Typography>

      <TableContainer sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px' }}>
        <Table size="small" aria-label="How the classification terms relate" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Concept</TableCell>
              <TableCell sx={headSx}>Motzu UI</TableCell>
              <TableCell sx={headSx}>Shopify admin</TableCell>
              <TableCell sx={headSx}>What it means</TableCell>
              <TableCell sx={headSx}>Example</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {concepts.map((row) => (
              <TableRow key={row.concept}>
                <TableCell sx={{ fontWeight: 600 }}>{row.concept}</TableCell>
                <TableCell>{row.motzu}</TableCell>
                <TableCell>{row.shopify}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>{row.meaning}</TableCell>
                <TableCell sx={{ ...v2Mono, wordBreak: 'break-word' }}>{row.example}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink.primary }}>Decision order</Typography>
        <Typography data-testid="mapping-decision-order" sx={{ fontSize: 13, color: tokens.ink.body, mt: 0.5 }}>
          {order.join(' → ')}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.5 }}>
          Motzu tries the steps in this order and the first match wins. It is a precedence chain, not a list of things
          to configure: set up only the steps your store needs.
        </Typography>
      </Box>

      {taxonomyOn && (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink.primary, mb: 0.75 }}>
            Example: Sony FX3
          </Typography>
          <TableContainer sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', maxWidth: 520 }}>
            <Table size="small" aria-label="FX3 example">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>FX3 example</TableCell>
                  <TableCell sx={headSx}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {FX3_EXAMPLE.map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell>{label}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.75 }}>
            The SKU matched a Store Taxonomy entry, so that entry set both Shopify fields and none of the later steps ran.
          </Typography>
        </Box>
      )}

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink.primary }}>The steps</Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: 13, color: tokens.ink.body }}>
        {taxonomyOn && (
          <li>
            <strong>Store Taxonomy</strong> matches the store SKU prefix to your own classification (CAM-121-00001 → entry CAM-121) and sets both Shopify fields. Use this when you already classify products before minting SKUs, as an ERP such as Cin7 does; then nothing below is needed.
          </li>
        )}
        {ruleCategoryOn ? [nameRulesStep, overridesStep] : [overridesStep, nameRulesStep]}
        <li>
          <strong>Unmatched Product Rules</strong> cover a product nothing above matched. Derive the type from the Motzu Classification name, use one default type, or flag it [UNMAPPED] so the products are easy to find in Shopify. They also set the Shopify Product Category sent when nothing else chose one.
        </li>
      </Box>
      <Typography sx={{ fontSize: 13, color: tokens.ink.body }}>
        <strong>Who owns these fields</strong> (Motzu owns / Fill only when blank / Never touch) sits above all of this and decides whether Motzu may write the field at all.
      </Typography>
      <Typography sx={{ fontSize: 13, color: tokens.ink.body }}>
        Not sure what a product will get? Enter its name, SKU and Motzu Classification in <strong>Try a product</strong> at the bottom to see the result and the step that decided it.
      </Typography>
    </Box>
  );
};

export default MappingExplainer;
